import { addMinutes } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useEventAccess } from '../../../../../../hooks/useEventAccess';
import { useMatching } from '../../../../../../hooks/useMatching';
import { usePermission } from '../../../../../../hooks/usePermission';
import { usePusher } from '../../../../../../hooks/usePusher';
import { useSounds } from '../../../../../../hooks/useSounds';
import { MessagingAttendee, UserMatch } from '../../../../../../interfaces/interfaces';
import { SpeedDatingComponentInterface, UiComponentElementInterface } from '../../../../../../interfaces/pagebuilder';
import { setPostRegistrationAction, setSpeeddating } from '../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector } from '../../../../../../reduxToolkit/typedRedux';
import { SPEEDDATINGSTATE } from '../../../../../../utils/enums';
import VyButton from '../../../../../common/VyButton';
import { useDevices } from '../../../../../meeting/hooks/useDevices';
import { TitleAndDescription } from '../../../../pageBuilder/TitleAndDescription';
import AttendeeAvatar from '../../AttendeeAvatar';
import { MatchList } from '../../matching/MatchList';
import { SpeedDatingControlBar } from './ControlBar';
import { PostCallModal } from './PostCallModal';
import { ScoreIndicator } from './ScoreIndicator';
import { SpeedDatingCountdown } from './SpeedDatingCountdown';
import { WaitingDisplay } from './WaitingDisplay';
import { WaitingForDisplay } from './WaitingForDisplay';

const SpeedDatingUiComponent = ({ uiComponent }: UiComponentElementInterface<SpeedDatingComponentInterface>) => {
  const { t } = useTranslation();
  const speedDating = useMatching(uiComponent);
  const dispatch = useDispatch();
  const router = useRouter();

  const { setupMatchingChannel, clearMatchingChannel } = usePusher();
  const sounds = useSounds();
  const matching = useMatching();
  const { isEventHost } = usePermission();
  const { requestDevices } = useDevices();
  const { loadUserData } = useEventAccess();

  const speedDatingState = useSelector(({ common }) => common.speeddating.state);
  const registration = useSelector(({ common }) => common.eventRegistration);
  const matchingChannel = useSelector(({ common }) => common.pusherChannels.matchingChannel);
  const videoDevice = useSelector(({ device }) => device.videoInputDevice);
  const audioDevice = useSelector(({ device }) => device.audioInputDevice);
  const history = useSelector(({ common }) => common.matchHistory);
  const user = useSelector(({ common }) => common.user);
  const event = useSelector(({ common }) => common.event);
  const pusher = useSelector((state) => state.common.pusher);

  const [currentMatch, setCurrentMatch] = useState<UserMatch | undefined>();
  const [isActionBlocked, setIsActionBlocked] = useState(false);

  const layoutDivRef = useCallback((node: any) => {
    setLayoutDiv(node);
  }, []);

  const [layoutDiv, setLayoutDiv] = useState<HTMLDivElement | undefined>();

  function getHeadersHeight(): number {
    const headers = document.getElementsByTagName('header');
    let height = 0;
    for (let i = 0; i < headers.length; i++) {
      height += headers.item(i)?.offsetHeight!;
    }
    return height;
  }

  const delayActions = () => {
    setIsActionBlocked(true);
    setTimeout(() => {
      setIsActionBlocked(false);
    }, 4000);
  };

  useEffect(() => {
    registration && !uiComponent?.disableHistory && matching.loadHistory();
  }, [registration]);

  useEffect(() => {
    speedDating.handleState(speedDatingState);
  }, [speedDatingState]);

  useEffect(() => {
    if (videoDevice?.deviceId && speedDating.publisher) speedDating.publisher.setVideoSource(videoDevice.deviceId);
  }, [videoDevice]);

  useEffect(() => {
    if (speedDating.publisher && audioDevice?.deviceId) speedDating.publisher.setAudioSource(audioDevice.deviceId);
  }, [audioDevice]);

  useEffect(() => {
    async function setup() {
      console.log(speedDatingState, registration);
      if (speedDatingState === SPEEDDATINGSTATE.WAITING && registration) {
        // Checking permissions
        const stream = await requestDevices();
        stream.getTracks().forEach((i) => i.stop());
        //Connect pusher
        console.log('setup matching channel');
        setupMatchingChannel(registration._id);
      } else if (speedDatingState === SPEEDDATINGSTATE.ENDING) {
        setCurrentMatch(undefined);
      } else {
        //Disconnect pusher
        clearMatchingChannel();
      }
    }
    console.log('starting setup');
    setup();
  }, [speedDatingState, registration, pusher]);

  useEffect(() => {
    if (!matchingChannel) return;
    console.log('Setting up matching channel listener');
    matchingChannel?.bind(`new-match`, (data: UserMatch) => {
      console.log('Vystem Realtime > New match data', data);
      sounds.playMatchingInvite();
      dispatch(setSpeeddating({ meetingId: data.room, state: SPEEDDATINGSTATE.CONNECTING }));
      setCurrentMatch(data);
      user && !uiComponent?.disableHistory && matching.loadHistory();
    });

    return () => {
      matchingChannel?.unbind('new-match');
    };
  }, [matchingChannel]);

  const onStart = () => {
    if (!event) return;
    if (registration) {
      speedDating.setState(SPEEDDATINGSTATE.WAITING);
    } else {
      if (event.modules.guestmanagement.settings.authUserAfterSignupWhenPossible === false)
        router.push(`${event?.eventauth.registerPageUrl}?redirect=${btoa(unescape(encodeURIComponent(router.asPath)))}`);
      else
        dispatch(
          setPostRegistrationAction(() => {
            loadUserData();
          })
        );
    }
  };

  return (
    <>
      <TitleAndDescription component={uiComponent!} />
      <div
        style={{
          color: uiComponent?.styling?.textColor || '#000',
        }}>
        <div className="flex flex-col items-center justify-center w-full">
          {speedDatingState !== SPEEDDATINGSTATE.CONNECTED && (
            <span className="w-full text-2xl font-bold">{uiComponent?.name ?? t('meeting:speeddating.title')}</span>
          )}
          {speedDatingState === SPEEDDATINGSTATE.CONNECTED && (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <AttendeeAvatar
                  attendee={
                    currentMatch?.participantA.registration._id === registration?._id
                      ? (currentMatch?.participantB.registration.user as MessagingAttendee)
                      : (currentMatch?.participantA.registration.user as MessagingAttendee)
                  }
                />
                <div className="ml-2 mr-4">
                  {currentMatch?.participantA.registration._id === registration?._id
                    ? currentMatch?.participantB.registration.user?.name
                    : currentMatch?.participantA.registration.user?.name}
                </div>
                <SpeedDatingCountdown
                  countdownEnd={addMinutes(
                    currentMatch?.datetime_last_match ? new Date(currentMatch?.datetime_last_match) : new Date(),
                    uiComponent?.duration ?? 0
                  )}
                  handleComplete={() => {
                    speedDating.setState(SPEEDDATINGSTATE.ENDING);
                    delayActions();
                  }}
                />
              </div>
            </div>
          )}
          <div
            className={`relative w-full ${
              speedDatingState !== SPEEDDATINGSTATE.INACTIVE && speedDatingState !== SPEEDDATINGSTATE.ENDING ? 'h-48 @sm:h-96 @sm:my-4' : ''
            }`}>
            {/* When I have the two conditions below in one line it breaks idk */}
            {speedDatingState === SPEEDDATINGSTATE.CONNECTED && layoutDiv?.children.length === 1 && <WaitingForDisplay match={currentMatch} />}
            {speedDatingState === SPEEDDATINGSTATE.CONNECTING && <WaitingForDisplay match={currentMatch} />}
            {speedDatingState === SPEEDDATINGSTATE.WAITING && <WaitingDisplay />}
            <div id="speedDatingLayout" className="absolute flex w-full h-full pointer-events-none" ref={layoutDivRef} />
          </div>
          <div className="h-12 p-1 m-4">
            {speedDatingState === SPEEDDATINGSTATE.INACTIVE && (
              <VyButton
                color={uiComponent?.customStyling?.buttonBackgroundColor}
                textColor={uiComponent?.customStyling?.buttonTextColor}
                disabled={isActionBlocked}
                showLoadingIcon={isActionBlocked}
                onClick={onStart}>
                {!registration ? <>{t('event:register')}</> : <>{uiComponent?.startButtonText || 'START'}</>}
              </VyButton>
            )}
            {speedDatingState === SPEEDDATINGSTATE.WAITING && (
              <VyButton
                color={uiComponent?.customStyling?.buttonBackgroundColor}
                textColor={uiComponent?.customStyling?.buttonTextColor}
                onClick={() => {
                  speedDating.setState(SPEEDDATINGSTATE.INACTIVE);
                  delayActions();
                }}>
                STOP
              </VyButton>
            )}
          </div>
          {speedDatingState === SPEEDDATINGSTATE.CONNECTED && (
            <SpeedDatingControlBar
              camActive={speedDating.camActive}
              onToggleCam={speedDating.toggleCam}
              onHighlight={() => {
                currentMatch && speedDating.toggleHighlight(currentMatch);
              }}
              onEndClick={() => {
                speedDating.setState(SPEEDDATINGSTATE.ENDING);
                delayActions();
              }}
            />
          )}
        </div>
        <PostCallModal
          open={speedDatingState === SPEEDDATINGSTATE.ENDING}
          onRateCall={(rating) => speedDating.rateCall(rating)}
          onContactAdd={() => console.log('add contact')}
          onNext={() => speedDating.setState(SPEEDDATINGSTATE.WAITING)}
          onEndMatching={() => speedDating.setState(SPEEDDATINGSTATE.INACTIVE)}
          ratingValue={speedDating.rating}
        />
        {registration && !uiComponent?.disableHistory && (
          <div className="flex flex-col">
            <span className="text-2xl font-bold">{t('meeting:speeddating.history.title')}</span>
            <span className="">{t('meeting:speeddating.history.description')}</span>
            <MatchList
              currentMatch={currentMatch}
              disableScore={uiComponent?.disableScore!}
              list={history}
              updateList={() => {
                user && !uiComponent?.disableHistory && matching.loadHistory();
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default SpeedDatingUiComponent;
